import { useState, useEffect } from "react";
import { toast } from "sonner";
import { X, Send, Copy, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function EmailComposer({ isOpen, onClose, initialContent = "", clients = [] }) {
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    to: "",
    subject: "",
    body: ""
  });

  // Parse initial content when it changes
  useEffect(() => {
    if (initialContent) {
      // Extract subject line
      const subjectMatch = initialContent.match(/Subject:\s*(.+?)(?:\n|$)/i);
      const subject = subjectMatch ? subjectMatch[1].trim() : "";
      
      // Remove the subject line from the body
      let body = initialContent.replace(/Subject:\s*.+?\n/i, '').trim();
      
      setFormData(prev => ({
        ...prev,
        subject,
        body
      }));
    }
  }, [initialContent]);

  const handleSendEmail = () => {
    if (!formData.to) {
      toast.error("Please enter a recipient email address");
      return;
    }

    // Create mailto link with all fields
    const mailtoLink = `mailto:${encodeURIComponent(formData.to)}?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(formData.body)}`;
    
    // Open default email client
    window.location.href = mailtoLink;
    toast.success("Opening email client...");
    onClose();
  };

  const handleCopyAll = async () => {
    const fullEmail = `To: ${formData.to}\nSubject: ${formData.subject}\n\n${formData.body}`;
    try {
      await navigator.clipboard.writeText(fullEmail);
      setCopied(true);
      toast.success("Email copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = fullEmail;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      toast.success("Email copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl" aria-describedby="email-composer-description">
        <DialogHeader>
          <DialogTitle className="text-indigo-500 flex items-center gap-2">
            <Send className="w-5 h-5 text-indigo-400" />
            Email Composer
          </DialogTitle>
          <p id="email-composer-description" className="sr-only">Compose and send your email</p>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* To Field */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">To *</label>
            <div className="flex gap-2">
              <input
                type="email"
                value={formData.to}
                onChange={(e) => setFormData({...formData, to: e.target.value})}
                className="flex-1 px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 focus:border-indigo-500 focus:outline-none"
                placeholder="client@example.com"
                data-testid="email-to-input"
              />
              {clients.length > 0 && (
                <select
                  onChange={(e) => setFormData({...formData, to: e.target.value})}
                  className="px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-slate-400 focus:border-indigo-500 focus:outline-none"
                  data-testid="email-client-select"
                >
                  <option value="">Select client</option>
                  {clients.filter(c => c.email).map((client) => (
                    <option key={client.id} value={client.email}>
                      {client.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Subject Field */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Subject</label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => setFormData({...formData, subject: e.target.value})}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 focus:border-indigo-500 focus:outline-none"
              placeholder="Email subject"
              data-testid="email-subject-input"
            />
          </div>

          {/* Body Field */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Message</label>
            <textarea
              value={formData.body}
              onChange={(e) => setFormData({...formData, body: e.target.value})}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 focus:border-indigo-500 focus:outline-none min-h-[250px] font-mono text-sm"
              placeholder="Email body..."
              data-testid="email-body-input"
            />
          </div>

          {/* Preview Info */}
          <div className="bg-slate-900/30 rounded-lg p-3 border border-slate-700">
            <p className="text-xs text-indigo-400">
              💡 Clicking "Open in Email" will launch your default email client with this email pre-filled.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-indigo-400 hover:text-slate-600 border border-slate-600 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCopyAll}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              data-testid="email-copy-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copied!" : "Copy All"}
            </button>
            <button
              type="button"
              onClick={handleSendEmail}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-colors"
              data-testid="email-send-btn"
            >
              <Send className="w-4 h-4" />
              Open in Email Client
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
